import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { RiCloseLine, RiShoppingBagLine, RiHeartLine, RiHeartFill, RiTimeLine, RiFireLine, RiStarFill } from 'react-icons/ri';
import './FoodPreviewModal.css';
import { useCart } from '../../context/CartContext.js';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import translationMenu from '../../data/translationMenu';
import Button from '../UI/Button/Button';
import LanguageContext from '../../context/LanguageContext.js';
import ProductCardImage from '../ProductCard/ProductCardImage';
import { getValidImageUrls } from '../../utils/productImage';

function FoodPreviewModal({ item, isOpen, onClose, currentLanguage, t }) {
    const { dir } = useContext(LanguageContext);
    const { formatPrice } = usePublicSettings();
    const { addToCart, addToWishlist, isInWishlist } = useCart();
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        if (isOpen && item?.variants && item.variants.length > 0) {
            setSelectedVariant(item.variants[0]);
        } else {
            setSelectedVariant(null);
        }
        setQuantity(1);
    }, [isOpen, item]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleEsc);

        return () => {
            document.body.style.overflow = originalOverflow;
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen, onClose]);

    if (!isOpen || !item) {
        return null;
    }

    const getLocalizedText = (field) => {
        if (!item[field]) {
            return '';
        }
        
        // Handle category and subCategory as strings with translations
        if (field === 'category') {
            return translationMenu[currentLanguage]?.categories?.[item[field]] || item[field];
        }
        if (field === 'subCategory') {
            return translationMenu[currentLanguage]?.subcategories?.[item[field]] || item[field];
        }
        
        // Handle size and servingSize.unit as simple strings
        if (field === 'size' || field === 'unit') {
            return item[field] || '';
        }
        
        // Handle other fields as objects with language keys
        return item[field][currentLanguage] || item[field].en || '';
    };

    const getTranslatedSize = (size) => {
        return translationMenu[currentLanguage]?.sizes?.[size] || size;
    };

    const getTranslatedUnit = (unit) => {
        return translationMenu[currentLanguage]?.units?.[unit] || unit;
    };

    const getTranslatedTag = (tag) => {
        return translationMenu[currentLanguage]?.tags?.[tag] || tag;
    };

    const getTranslatedIngredient = (ingredient) => {
        return translationMenu[currentLanguage]?.ingredients?.[ingredient] || ingredient;
    };

    const getTranslatedAllergen = (allergen) => {
        return translationMenu[currentLanguage]?.ingredients?.[allergen] || allergen;
    };

    const getLocalizedArray = (field) => {
        if (!item[field]) {
            return [];
        }
        
        // Handle ingredients, allergens, tags as simple arrays
        if (field === 'ingredients' || field === 'allergens' || field === 'tags') {
            return Array.isArray(item[field]) ? item[field] : [];
        }
        
        // Handle legacy language-specific arrays
        return Array.isArray(item[field]?.[currentLanguage])
            ? item[field][currentLanguage]
            : item[field]?.en || [];
    };

    const localizedIngredients = getLocalizedArray('ingredients');
    const localizedAllergens = getLocalizedArray('allergens');
    const localizedTags = getLocalizedArray('tags');
    const displayName = getLocalizedText('name');
    const variantImageUrls = getValidImageUrls(selectedVariant?.images);
    const imageProduct = variantImageUrls.length > 0
        ? { imageUrl: variantImageUrls }
        : item;

    // Get current price (from selected variant or base price)
    const currentPrice = selectedVariant?.price || item.price;

    // Check availability (handle both old and new field names)
    const isAvailable = item.availability !== undefined ? item.availability : item.isAvailable;

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const handleAddToCart = () => {
        const itemToAdd = selectedVariant
            ? { ...item, selectedVariant, price: selectedVariant.price, quantity }
            : { ...item, quantity };
        addToCart(itemToAdd);
        onClose();
    };

    const handleAddToWishlist = () => {
        addToWishlist(item);
    };

    const wishlistIdentifier = item._id || item.id;

    const handleQuantityChange = (increment) => {
        const maxQuantity = item.quantity || 99;
        const newQuantity = quantity + increment;

        if (newQuantity >= 1 && newQuantity <= maxQuantity) {
            setQuantity(newQuantity);
        }
    };

    return (
        <div
            className="food-modal-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={handleBackdropClick}
            dir={dir()}
        >
            <div className="food-modal-card">
                <button className="food-modal-close" onClick={onClose} type="button" aria-label={t('menu.modal.close')}>
                    <RiCloseLine />
                </button>

                <div className="food-modal-img-wrapper">
                    <ProductCardImage
                        product={imageProduct}
                        productName={displayName}
                        alt={displayName}
                        className="food-modal-img"
                    />
                    {item.isSpecial && <span className="food-modal-badge special">{t('menu.modal.special')}</span>}
                    {item.isNewItem && <span className="food-modal-badge new">{t('menu.modal.new')}</span>}
                </div>

                <div className="food-modal-body">
                    <div className="food-modal-header">
                        <div className="category-info">
                            <span className="food-modal-category">{getLocalizedText('category')}</span>
                            {item.subCategory && (
                                <>
                                    <span className="category-separator">›</span>
                                    <span className="food-modal-subcategory">{getLocalizedText('subCategory')}</span>
                                </>
                            )}
                        </div>
                        {item.rating && (
                            <div className="food-modal-rating">
                                <RiStarFill className="star-icon" />
                                <span>{item.rating}</span>
                                {item.reviewCount && <span className="review-count">({item.reviewCount})</span>}
                            </div>
                        )}
                    </div>

                    <h3 className="food-modal-title">{getLocalizedText('name')}</h3>

                    {/* Dietary badges */}
                    {(item.isVegetarian || item.isVegan || item.isGlutenFree) && (
                        <div className="food-modal-dietary-badges">
                            {item.isVegan && <span className="dietary-badge vegan">{t('menu.modal.vegan')}</span>}
                            {item.isVegetarian && !item.isVegan && <span className="dietary-badge vegetarian">{t('menu.modal.vegetarian')}</span>}
                            {item.isGlutenFree && <span className="dietary-badge gluten-free">{t('menu.modal.glutenFree')}</span>}
                        </div>
                    )}

                    {item.description && (
                        <p className="food-modal-description">{getLocalizedText('description')}</p>
                    )}

                    {/* Quick info */}
                    {(item.preparationTime || item.calories || selectedVariant?.calories || item.servingSize || item.spiceLevel !== 'none') && (
                        <div className="food-modal-quick-info">
                            {item.preparationTime && (
                                <div className="info-item">
                                    <RiTimeLine />
                                    <span>{item.preparationTime} {t('menu.modal.minutes') || 'min'}</span>
                                </div>
                            )}
                            {(selectedVariant?.calories || item.calories) && (
                                <div className="info-item">
                                    <RiFireLine />
                                    <span>{selectedVariant?.calories || item.calories} {t('menu.modal.calories') || 'cal'}</span>
                                </div>
                            )}
                            {(selectedVariant?.servingSize || item.servingSize) && (
                                <div className="info-item">
                                    <span className="serving-icon">📏</span>
                                    <span>
                                        {(selectedVariant?.servingSize?.value || item.servingSize?.value)}
                                        {' '}{getTranslatedUnit(selectedVariant?.servingSize?.unit || item.servingSize?.unit)}
                                    </span>
                                </div>
                            )}
                            {item.spiceLevel && item.spiceLevel !== 'none' && (
                                <div className="info-item spice-level">
                                    <span className={`spice-indicator ${item.spiceLevel}`}>
                                        {item.spiceLevel === 'mild' && '🌶️'}
                                        {item.spiceLevel === 'medium' && '🌶️🌶️'}
                                        {item.spiceLevel === 'hot' && '🌶️🌶️🌶️'}
                                        {item.spiceLevel === 'very-hot' && '🌶️🌶️🌶️🌶️'}
                                    </span>
                                    <span className="spice-label">
                                        {item.spiceLevel === 'mild' && (t('menu.modal.spiceMild') || 'Mild')}
                                        {item.spiceLevel === 'medium' && (t('menu.modal.spiceMedium') || 'Medium')}
                                        {item.spiceLevel === 'hot' && (t('menu.modal.spiceHot') || 'Hot')}
                                        {item.spiceLevel === 'very-hot' && (t('menu.modal.spiceVeryHot') || 'Very Hot')}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {localizedIngredients?.length > 0 && (
                        <div className="food-modal-section">
                            <h4>{t('menu.modal.ingredients') || 'Ingredients'}</h4>
                            <ul className="ingredients-list">
                                {localizedIngredients.map((ingredient, index) => (
                                    <li key={`${ingredient}-${index}`}>{getTranslatedIngredient(ingredient)}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {localizedAllergens?.length > 0 && (
                        <div className="food-modal-section allergens">
                            <h4>{t('menu.modal.allergens') || 'Allergen Information'}</h4>
                            <p className="allergens-warning">
                                ⚠️ {t('menu.modal.contains')}: {localizedAllergens.map(allergen => getTranslatedAllergen(allergen)).join(', ')}
                            </p>
                        </div>
                    )}

                    {Array.isArray(item.variants) && item.variants.length > 0 && (
                        <div className="food-modal-section">
                            <h4>{t('menu.modal.sizes') || 'Available Sizes'}</h4>
                            <div className="variants-grid">
                                {item.variants.map((variant, index) => (
                                    <button
                                        key={`${variant.size}-${index}`}
                                        className={`variant-option ${selectedVariant === variant ? 'selected' : ''} ${!variant.isAvailable ? 'unavailable' : ''}`}
                                        onClick={() => variant.isAvailable && setSelectedVariant(variant)}
                                        disabled={!variant.isAvailable}
                                        type="button"
                                    >
                                        <div className="variant-size">{getTranslatedSize(variant.size)}</div>
                                        <div className="variant-price">{formatPrice(variant.price)}</div>
                                        {variant.diameter && <div className="variant-diameter">{variant.diameter}cm</div>}
                                        {variant.calories && <div className="variant-calories">{variant.calories} cal</div>}
                                        {!variant.isAvailable && <div className="variant-unavailable">{t('menu.modal.unavailableShort')}</div>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {localizedTags?.length > 0 && (
                        <div className="food-modal-tags">
                            {localizedTags.map((tag, index) => (
                                <span key={`${tag}-${index}`} className="tag">{getTranslatedTag(tag)}</span>
                            ))}
                        </div>
                    )}

                    <div className="food-modal-actions">
                        <div className="price-and-stock">
                            <span className="food-modal-price">
                                {isAvailable && item.status === 'active' ? formatPrice(currentPrice * quantity) : t('menu.modal.unavailable')}
                            </span>
                            {currentPrice && quantity > 1 && (
                                <span className="unit-price">
                                    {formatPrice(currentPrice)} {t('menu.modal.each') || 'each'}
                                </span>
                            )}
                            {item.quantity !== undefined && item.quantity > 0 && item.quantity <= 10 && item.status === 'active' && (
                                <span className="low-stock-warning">
                                    ⚡ {t('menu.modal.onlyLeft') || 'Only'} {item.quantity} {t('menu.modal.left') || 'left'}
                                </span>
                            )}
                        </div>
                        {isAvailable && item.status === 'active' && (
                            <div className="food-modal-buttons">
                                <div className="quantity-selector">
                                    <button
                                        type="button"
                                        className="quantity-btn"
                                        onClick={() => handleQuantityChange(-1)}
                                        disabled={quantity <= 1}
                                        aria-label={t('menu.modal.decreaseQuantity') || 'Decrease quantity'}
                                    >
                                        -
                                    </button>
                                    <span className="quantity-value">{quantity}</span>
                                    <button
                                        type="button"
                                        className="quantity-btn"
                                        onClick={() => handleQuantityChange(1)}
                                        disabled={quantity >= (item.quantity || 99)}
                                        aria-label={t('menu.modal.increaseQuantity') || 'Increase quantity'}
                                    >
                                        +
                                    </button>
                                </div>
                                <Button type="button" flex className="cart-modal-button" onClick={handleAddToCart}>
                                    <RiShoppingBagLine className='cart-modal-icon' />
                                    <span>{t('menu.modal.addToCart') || 'Add to Cart'}</span>
                                </Button>
                                <Button
                                    type="button"
                                    className={`wishlist-modal-button ${isInWishlist(wishlistIdentifier) ? 'active' : ''}`}
                                    onClick={handleAddToWishlist}
                                    aria-label={isInWishlist(wishlistIdentifier) ? t('menu.modal.inWishlist') : t('menu.modal.addToWishlist')}
                                >
                                    {isInWishlist(wishlistIdentifier) ? <RiHeartFill className='wishlist-modal-icon' /> : <RiHeartLine className='wishlist-modal-icon' />}
                                </Button>
                            </div>
                        )}
                        {(item.status === 'out-of-stock' || (!isAvailable && item.status === 'active')) && (
                            <div className="out-of-stock-message">
                                {t('menu.modal.outOfStock') || 'Currently Out of Stock'}
                            </div>
                        )}
                        {item.status === 'inactive' && (
                            <div className="inactive-message">
                                {t('menu.modal.noLongerAvailable') || 'This item is no longer available'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

FoodPreviewModal.propTypes = {
    item: PropTypes.object,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentLanguage: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired
};

export default FoodPreviewModal;

